import { Check, ChevronsUpDown, Loader2Icon } from 'lucide-react';
import { useId, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Field, FieldLabel } from '@/components/ui/field';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { useFieldSelections } from '@/hooks/useFieldSelections';
import { cn } from '@/lib/utils';

interface EditDynamicSelectFieldProps {
  fieldClassName?: string;
  fieldName: string;
  label: string;
  labelClassName?: string;
  model: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  value: string;
}

/**
 * A select field that dynamically fetches its options from the backend
 * with search functionality
 */
export function EditDynamicSelectField({
  fieldClassName,
  fieldName,
  label,
  labelClassName,
  model,
  onChange,
  placeholder = 'Select an option',
  required = false,
  value
}: EditDynamicSelectFieldProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const triggerId = useId();
  const { getLabel, loading, options } = useFieldSelections(model, fieldName);

  // Filter options based on search term (client-side filtering)
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const lowerSearch = searchTerm.toLowerCase();
    return options.filter(option =>
      option.label.toLowerCase().includes(lowerSearch)
    );
  }, [options, searchTerm]);

  const selectedLabel = value ? getLabel(value) : null;

  return (
    <Field className={cn('gap-1.5', fieldClassName)}>
      <FieldLabel
        className={labelClassName}
        htmlFor={triggerId}
      >
        {label}
        {required && <span className="text-red-500">*</span>}
      </FieldLabel>
      <Popover
        modal={true}
        onOpenChange={setOpen}
        open={open}
      >
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            className="dark:bg-input/30 dark:hover:bg-input/50 h-9 w-full justify-between bg-transparent font-normal"
            disabled={loading}
            id={triggerId}
            role="combobox"
            variant="outline"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <Loader2Icon className="h-4 w-4 animate-spin" />
                <span className="text-muted-foreground">Loading...</span>
              </div>
            ) : (
              <span className={cn(!selectedLabel && 'text-muted-foreground')}>
                {selectedLabel ?? placeholder}
              </span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-0"
        >
          <Command shouldFilter={false}>
            <CommandInput
              onValueChange={setSearchTerm}
              placeholder="Search..."
              value={searchTerm}
            />
            <CommandList>
              <CommandGroup>
                {/* Empty option to allow clearing */}
                <CommandItem
                  onSelect={() => {
                    onChange('');
                    setOpen(false);
                    setSearchTerm('');
                  }}
                  value="__none__"
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      !value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="text-gray-500">None</span>
                </CommandItem>
                {filteredOptions.length === 0 ? (
                  <div className="py-4 text-center text-sm text-gray-500">
                    No options found.
                  </div>
                ) : (
                  filteredOptions.map(option => (
                    <CommandItem
                      key={option.value}
                      onSelect={() => {
                        onChange(option.value);
                        setOpen(false);
                        setSearchTerm('');
                      }}
                      value={option.value}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === option.value ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </Field>
  );
}
